import React, { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ToastContext } from "../../../contexts/ToastContext/ToastContext";
import { AppDispatch, RootState } from "../../../redux/store";
import { UserType } from "../../../services/user.service";
import Button from "../../Button/Button";
import Checkbox from "../../Checkbox/Checkbox";
import Select from "../../Select/Select";
import TextInput from "../../TextInput/TextInput";
import { AuthPermission } from "../../../services/auth.service";
import { Office } from "../../../models/office.model";
import { useDispatch } from "react-redux";
import { updatePatientUserProfile, getUser } from "../../../redux/slices/CurrentUserSlice";
import { ToastVariants, FileTypes } from "../../../models";
import SelectNew from "../../Select/SelectNew";
import { getJobTitles } from "../../../redux/slices/UserSlice";
import FormErrorMessage from "../../FormErrorMessage";
import DropArea from "../../DropArea/DropArea";
import { onFileError } from "../../../utils";
import { deleteUserFile, getFileData, getUserHeadLogo } from "../../../redux/slices/OfficeSlice";
import { DeleteOutline } from "@mui/icons-material";
import { colors } from "../../../utils/constants";

interface EditFormDataType {
  id: string;
  label: string;
  placeholder: string;
  value: any;
  errorMsg: string;
  isError: boolean;
  required: boolean;
  type: "text" | "select" | "phone" | "select-new" | "file";
  options?: string[];
}

const roleOptions = [
  {
    title: "System Superuser",
    key: UserType.SystemAdmin,
  },
  {
    title: "Office Superuser",
    key: UserType.OfficeAdmin,
  },
  {
    title: "Accounting",
    key: UserType.Accounting,
  },
  {
    title: "User",
    key: UserType.User,
  },
];

let checkBoxOptions = [
  {
    title: "Edit Office",
    key: AuthPermission.Office,
    checked: false,
    dependents: [],
    disabled: false,
  },
  {
    title: "Edit Users",
    key: AuthPermission.User,
    checked: false,
    dependents: [],
    disabled: false,
  },
  {
    title: "Edit Services",
    key: AuthPermission.Service,
    checked: false,
    dependents: [],
    disabled: false,
  },
  {
    title: "Sign Forms",
    key: AuthPermission.Signing,
    checked: false,
    dependents: [],
    disabled: false,
  },
  {
    title: "View Reports",
    key: AuthPermission.Report,
    checked: false,
    dependents: [],
    disabled: false,
  },
  {
    title: "View/Edit Finance",
    key: AuthPermission.Finance,
    checked: false,
    dependents: [],
    disabled: false,
  },
  {
    title: "Edit Requests",
    key: AuthPermission.Request,
    checked: false,
    dependents: [],
    disabled: false,
  },
  {
    title: "Legal",
    key: AuthPermission.Legal,
    checked: false,
    dependents: [],
    disabled: false,
  },
];

interface FileDataType {
  fileId: string;
  path: string;
  originalFilename: string;
  contentType: string;
  size: number;
  url: string;
  urlExpires: string;
  createdDate: string;
}

const EditProfile = ({ onCancelClick }: { onCancelClick: () => void }) => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector((state: RootState) => state.currentUser.currentUser);
  const { jobTitles } = useSelector((state: RootState) => state.user);
  const { pssData } = useSelector((state: RootState) => state.currentUser);

  const getIsPCP = (offices: Office[] | undefined, userId: string) => {
    if (!offices) return false;
    for (let i = 0; i < offices.length; i++) {
      const selectedOffice = offices[i];
      if (selectedOffice.primaryCareProviders?.some(pcp => pcp.userId === userId)) {
        return true;
      }
    }
    return false;
  };

  const [primaryCareProvider, setPrimaryCareProvider] = useState<boolean>(getIsPCP(user?.offices, user?.userId || ""));
  const [formData, setFormData] = useState<EditFormDataType[]>([]);
  const [formDataOld, setFormDataOld] = useState<EditFormDataType[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorInitiated, setErrorInitiated] = useState(false);
  const [dropAreaError, setDropAreaError] = useState<string>("");
  const [letterLogoDropAreaError, setLetterLogoDropAreaError] = useState<string>("");
  const toast = useContext(ToastContext);
  const { currentOffice } = useSelector((state: RootState) => state.office);

  const [fileData, setFileData] = useState<FileDataType | undefined>();
  const [letterLogoFileData, setLetterLogoFileData] = useState<FileDataType | undefined>();
  const [signImageFileId, setSignImageFileId] = useState<string | undefined>(user?.signImageFileId);
  const [letterHeaderLogoFileId, setLetterHeaderLogoFileId] = useState<string | undefined>(user?.letterHeaderLogo);
  const [activeUploadField, setActiveUploadField] = useState<string | null>(null);

  // Fetch user data when component mounts
  useEffect(() => {
    dispatch(getUser());
  }, [dispatch]);

  useEffect(() => {
    setPrimaryCareProvider(getIsPCP(user?.offices, user?.userId || ""));
  }, [dispatch, user]);

  useEffect(() => {
    const officeId = currentOffice?.officeId || user?.offices?.[0]?.officeId;
    if (user && officeId && primaryCareProvider) {
      dispatch(
        getFileData({
          officeId: officeId,
          userID: user?.userId,
        })
      )
        .then(action => {
          setFileData(action.payload as FileDataType);
        })
        .catch(error => {});
    }
  }, [dispatch, user, primaryCareProvider]);

  useEffect(() => {
    if (user?.letterHeaderLogo) {
      // Fetch letter logo file data if it exists
      dispatch(getUserHeadLogo({ userID: user?.userId }))
        .then(action => {
          if (action.payload) {
            setLetterLogoFileData(action.payload as FileDataType);
          }
        })
        .catch(error => {});
    }
  }, [user?.letterHeaderLogo]);

  const isEdited = JSON.stringify(formData) === JSON.stringify(formDataOld);

  const permission = user?.permissions;
  if (permission) {
    checkBoxOptions = checkBoxOptions?.map(p => {
      return { ...p, checked: permission?.includes(p.key) };
    });
  }

  const handleChange = (id: string, value: string | File) => {
    const updated = formData.map(item => {
      const updatedItem = { ...item };
      if (updatedItem.id === id) {
        if (id === "mpcNumber") {
          // Allow only alphanumeric characters
          const alphanumericValue = typeof value === "string" ? value.replace(/[^a-zA-Z0-9]/g, "") : value;
          // Limit to 12 characters max
          const limitedValue =
            typeof alphanumericValue === "string" && alphanumericValue.length > 12 ? alphanumericValue.slice(0, 12) : alphanumericValue;
          updatedItem.value = limitedValue;
          // Validate: required but no minimum length, max 12, alphanumeric only
          const trimmedValue = typeof limitedValue === "string" ? limitedValue.trim() : "";
          updatedItem.isError = trimmedValue === "" || (trimmedValue.length > 0 && !/^[a-zA-Z0-9]+$/.test(trimmedValue)) || trimmedValue.length > 12;
        } else if (id === "signImageFileId") {
          updatedItem.value = value;
          updatedItem.isError = !(value instanceof File);
        } else if (id === "letterHeaderLogo") {
          updatedItem.value = value;
          updatedItem.isError = !(value instanceof File);
        } else {
          updatedItem.value = value;
          updatedItem.isError = updatedItem.required && typeof value === "string" && value.trim() === "";
        }
      }
      return updatedItem;
    });
    setFormData(updated);
  };

  const validateForm = () => {
    return formData.every(v => {
      return !v.isError;
    });
  };
  const handleUpdate = async () => {
    const officeId = user?.offices?.[0]?.officeId;
    const userId = user?.userId;
    if (!officeId || !userId) return;

    setLoading(true);

    const body: any = {
      officeId,
      userId,
      firstName: formData[0].value,
      lastName: formData[1].value,
      email: user.email,
      phone: user.phone,
    };

    if (user?.userType !== UserType.SystemAdmin) {
      body.jobTitleId = formData[3].value;
    }

    if (primaryCareProvider) {
      body.mpcNumber = formData.find(f => f.id === "mpcNumber")?.value || "";
      body.pcpCredentials = formData.find(f => f.id === "pcpCredentials")?.value || "";

      // Prefer newly uploaded signature fileId, otherwise fall back to existing one on user
      if (signImageFileId) {
        body.signImageFileId = signImageFileId;
      } else if (user?.signImageFileId) {
        body.signImageFileId = user.signImageFileId;
      }
    }

    // Upload letter logo file if it's a new File
    // Note: Letter logo should be uploaded as-is (PNG/JPG/JPEG), not converted to PDF
    // Letter logo: use uploaded fileId if present, otherwise keep existing one
    if (letterHeaderLogoFileId) {
      body.letterHeaderLogo = letterHeaderLogoFileId;
    } else if (user?.letterHeaderLogo) {
      body.letterHeaderLogo = user.letterHeaderLogo;
    }

    await submitProfile(body, officeId, userId);
  };

  const submitProfile = async (body: any, officeId: string, userId: string) => {
    const res = await dispatch(updatePatientUserProfile({ body, officeId, userId }));
    if (res.payload) {
      toast?.openToast("Profile updated successfully!", 2000, ToastVariants.Success);
      onCancelClick();
    } else {
      toast?.openToast("Profile update failed", 2000, ToastVariants.Error);
    }
    setLoading(false);
  };

  const deleteFile = (isLetterLogo: boolean) => {
    if (user?.userId) {
      const res = dispatch(
        deleteUserFile({
          userID: user?.userId,
          type: isLetterLogo ? "letterheaderlogo" : "signature",
        })
      ).then(action => {
        if (action?.payload) {
          onCancelClick();
          dispatch(getUser());
          toast?.openToast("File deleted successfully.", 2000, ToastVariants.Success);
        } else toast?.openToast("Something went wrong.", 2000, ToastVariants.Error);
      });
    }
  };

  const renderInputFields = () => {
    const items = formData.map(form => {
      const commonProps = {
        onChangeFunc: (e: any) => handleChange(form.id, e.currentTarget.value),
        value: form.value,
        placeholder: form.placeholder,
        errorMsg: form.errorMsg,
        isRequired: form.required,
        isError: form.isError && errorInitiated,
        label: form.label,
        isDarkBg: true,
        key: form.id,
      };
      switch (form.type) {
        case "text":
        case "phone":
          return (
            <div className="py-2" key={form.id}>
              <TextInput
                {...commonProps}
                onBlur={form.type === "text" ? e => handleChange(form.id, e.currentTarget.value.trim()) : undefined}
                maxLength={form.id === "mpcNumber" ? 12 : undefined}
              />
            </div>
          );
        case "select":
          return (
            <div className="py-2" key={form.id}>
              <Select
                onSelect={e => handleChange(form.id, e.currentTarget.value)}
                options={form.options!}
                required={form.required}
                title={form.label}
                placeHolder={form.placeholder}
                disabled={true}
                isDarkBg={true}
                //   defaultValue={form.value}
              />
            </div>
          );
        case "select-new":
          return (
            <div className="py-2">
              <SelectNew
                title={form.label}
                required={true}
                value={commonProps.value}
                placeHolder={form.placeholder}
                onSelect={e => handleChange(form.id, e.target.value)}
                options={jobTitles.map(item => {
                  return { label: item.name, value: item.jobTitleId };
                })}
                disabled={true}
                errorMsg={commonProps.isError ? commonProps.errorMsg : ""}
                isDarkBg
              />
            </div>
          );
        case "file":
          const isLetterLogo = form.id === "letterHeaderLogo";
          const allowedTypes = isLetterLogo ? [FileTypes.PNG, FileTypes.JPEG, FileTypes.JPG] : [FileTypes.PNG, FileTypes.JPG, FileTypes.JPEG];
          const maxSize = isLetterLogo ? 2 * 1024 * 1024 : 5 * 1024 * 1024; // 2MB for letter logo, 100KB for signature
          const dropAreaText = isLetterLogo ? "PNG, JPG, or JPEG logo (max 2MB)" : "PNG or JPG signature (max 5MB)";
          const fileDataToShow = isLetterLogo ? letterLogoFileData : fileData;
          const noFileText = isLetterLogo ? "No letter logo uploaded" : "No signature uploaded";

          return (
            <div className="py-2" key={form.id}>
              <label className="text-sm text-docsigna-blue-dark font-medium block w-full max-w-full mb-1">
                {form.label}
                {form.required && <span className="text-red-500">*</span>}
              </label>
              {isLetterLogo && <label>Please upload a logo with dimensions 125 × 125 pixels .</label>}

              <DropArea
                allowedFileTypes={allowedTypes}
                maxFileSize={maxSize}
                maxNumOfFiles={1}
                onFilesChange={fileIds => {
                  // `DropArea` uploads the file via files API and gives us back the fileIds.
                  // Use the "activeUploadField" to map the latest ID to the correct state.
                  if (!fileIds.length || !activeUploadField) return;
                  const latestId = fileIds[fileIds.length - 1];

                  if (activeUploadField === "letterHeaderLogo") {
                    setLetterHeaderLogoFileId(latestId);
                  } else if (activeUploadField === "signImageFileId") {
                    setSignImageFileId(latestId);
                  }
                }}
                handleFileChange={file => {
                  // Mark which field triggered this upload
                  setActiveUploadField(form.id);

                  handleChange(form.id, file); // updates form value

                  setFormData(prev =>
                    prev.map(f =>
                      f.id === form.id
                        ? {
                            ...f,
                            isError: false,
                            errorMsg: "",
                            value: file, // just to be safe
                          }
                        : f
                    )
                  );
                  // Only reset error display for this field
                  setErrorInitiated(false);
                }}
                onError={err => {
                  if (isLetterLogo) {
                    setLetterLogoDropAreaError(onFileError(err, "2MB"));
                  } else {
                    setDropAreaError(onFileError(err, "100kb"));
                  }
                }}
                dropAreaText={dropAreaText}
                isDarkBg
                isLocalUpload
              />
              {form.isError && errorInitiated && <FormErrorMessage text={form?.errorMsg} />}
              {isLetterLogo
                ? letterLogoDropAreaError && <p className="text-red-500 text-sm mt-1">{letterLogoDropAreaError}</p>
                : dropAreaError && <p className="text-red-500 text-sm mt-1">{dropAreaError}</p>}
              <div className="text-sm">
                {fileDataToShow ? (
                  <div className="mt-3 flex justify-between ">
                    <span>
                      <b>FileName: </b>
                      {fileDataToShow?.originalFilename}
                    </span>
                    <span>
                      <DeleteOutline htmlColor={colors.Orange} className="cursor-pointer" onClick={() => deleteFile(isLetterLogo)} />
                    </span>
                  </div>
                ) : (
                  noFileText
                )}
              </div>
            </div>
          );
        default:
          return null;
      }
    });

    return items;
  };

  useEffect(() => {
    if (user) {
      const officeName = user?.offices?.length ? user?.offices[0].name : "";
      const formDataInit: EditFormDataType[] = [
        {
          id: "firstName",
          label: "First Name",
          placeholder: "Enter first name",
          value: user?.firstName || "",
          errorMsg: "Please enter first name.",
          isError: false,
          required: true,
          type: "text",
        },
        {
          id: "lastName",
          label: "Last Name",
          placeholder: "Enter last name",
          value: user?.lastName || "",
          errorMsg: "Please enter last name.",
          isError: false,
          required: true,
          type: "text",
        },
        {
          id: "office",
          label: "Office",
          placeholder: officeName || "Select Office",
          value: officeName,
          errorMsg: "Please select an office.",
          isError: false,
          required: false,
          type: "select",
          options: [],
        },
      ];
      if (user?.userType !== UserType.SystemAdmin) {
        formDataInit.push({
          id: "jobTitle",
          label: "Job Title",
          placeholder: "Select job title",
          value: user?.jobTitle?.jobTitleId ?? "",
          errorMsg: "Please select job title",
          isError: !user?.jobTitle,
          required: true,
          type: "select-new",
          options: [],
        });
      }
      // Add Letter Logo field for all users
      formDataInit.push({
        id: "letterHeaderLogo",
        label: "Letter Head Logo",
        placeholder: "Upload letter logo",
        value: user?.letterHeaderLogo || "",
        errorMsg: "Letter logo is required.",
        isError: false,
        required: false,
        type: "file",
      });
      if (primaryCareProvider && pssData?.officePss) {
        formDataInit.push(
          {
            id: "mpcNumber",
            label: "Medical practitioner Number (CPSO, CPSBC, CPSNS etc)",
            placeholder: "Enter medical practitioner number",
            value: user?.mpcNumber || "",
            errorMsg: "MPN is required and can only contain letters and numbers (max 12 characters).",
            isError: false,
            required: true,
            type: "text",
          },
          {
            id: "pcpCredentials",
            label: "PCP Credentials",
            placeholder: "Enter PCP Credentials",
            value: user?.pcpCredentials || "",
            errorMsg: "MPN is required and must be 10 digits.",
            isError: false,
            required: false,
            type: "text",
          },
          {
            id: "signImageFileId",
            label: "Signature",
            placeholder: "Upload signature",
            value: user?.signImageFileId || "",
            errorMsg: "Signature is required.",
            isError: false,
            required: true,
            type: "file", // <-- change here
          }
        );
      }

      setFormDataOld([...formDataInit]);
      setFormData([...formDataInit]);
    }
    dispatch(getJobTitles());
    setPrimaryCareProvider(getIsPCP(user?.offices, user?.userId || ""));
  }, [user, pssData]);

  const validateAllFields = () => {
    const updated = formData.map(item => {
      const updatedItem = { ...item };
      if (updatedItem.required) {
        if (updatedItem.id === "mpcNumber") {
          const trimmedValue = typeof updatedItem.value === "string" ? updatedItem.value.trim() : "";
          // Required validation
          if (trimmedValue === "") {
            updatedItem.isError = true;
          } else {
            // Check if alphanumeric only
            const isAlphanumeric = /^[a-zA-Z0-9]+$/.test(trimmedValue);
            // Check max length (12)
            const isWithinLimit = trimmedValue.length <= 12;
            updatedItem.isError = !isAlphanumeric || !isWithinLimit;
          }
        } else if (updatedItem.id === "signImageFileId") {
          if (fileData?.originalFilename) {
            updatedItem.isError = false;
          } else {
            updatedItem.isError = !(updatedItem.value instanceof File);
          }
        } else if (updatedItem.id === "letterHeaderLogo") {
          if (letterLogoFileData?.originalFilename) {
            updatedItem.isError = false;
          } else {
            updatedItem.isError = !(updatedItem.value instanceof File);
          }
        } else {
          updatedItem.isError = updatedItem.required && typeof updatedItem.value === "string" && updatedItem.value.trim() === "";
        }
      }
      return updatedItem;
    });
    setFormData(updated);
    // Return true if all fields are valid
    return updated.every(item => !item.isError);
  };

  return (
    <>
      <div className="px-8">
        {renderInputFields()}
        <div className="py-2">
          <label className="text-sm text-docsigna-blue-dark font-medium block w-full mb-1">Role</label>
          <Checkbox
            onChange={() => {}}
            label={roleOptions.find(v => v.key === user?.userType)?.title || ""}
            checked={true}
            disabled={true}
            isDarkBg={true}
          />
        </div>
        <div className="py-2">
          <label className="text-sm text-docsigna-blue-dark font-medium block w-full mb-1">Permissions</label>
          {checkBoxOptions?.map(v => {
            return <Checkbox label={v.title} checked={v.checked} onChange={() => {}} key={v.key} disabled={true} isDarkBg={true} />;
          })}
          <Checkbox label="Primary Care Provider" checked={primaryCareProvider} onChange={() => null} disabled={true} isDarkBg={true} />
        </div>
      </div>
      <div className="fixed bottom-0 right-0 w-11/12 max-w-120 z-30 bg-docsigna-pink-light">
        <div className="px-8 py-5 flex justify-end">
          <Button
            onClick={() => {
              onCancelClick();
            }}
            className="pointer px-5 mr-3"
            text="Cancel"
            width="fit"
            variant="Secondary"
          />
          <div onClick={() => setErrorInitiated(true)}>
            <Button
              onClick={() => {
                setErrorInitiated(true);
                if (validateAllFields()) handleUpdate();
              }}
              className="pointer px-5"
              disabled={(!validateForm() && errorInitiated) || isEdited}
              text="Save"
              width="fit"
              variant={loading ? "loading" : "Primary"}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default EditProfile;
